// FE-SCRM-054 — ⌘+K global-search modal.
//
// Self-contained: mounts itself, listens for ⌘+K (Mac) / Ctrl+K
// (Win) to open, Escape to close. Inside, typing is debounced
// 150ms before the query fires; results render grouped by entity
// type with arrow-key navigation + Enter to select.
//
// Recent searches (last 5) persist to localStorage so the producer
// returning to the modal sees their last hits instead of a blank
// slate. Selecting a result both navigates (via react-router) and
// pushes the row onto the recent list.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ENTITY_TYPE_LABEL,
  ENTITY_TYPE_ORDER,
  type SearchEntityType,
  type SearchResultRow,
} from "./types";
import {
  addRecentSearch,
  loadRecentSearches,
  type RecentSearchEntry,
} from "./recentSearches";
import { useGlobalSearch } from "./useGlobalSearch";


const DEBOUNCE_MS = 150;


interface FlatRow {
  entity_type: SearchEntityType;
  row: SearchResultRow;
}


function _flatten(
  data: ReturnType<typeof useGlobalSearch>["data"],
): FlatRow[] {
  if (!data) return [];
  const flat: FlatRow[] = [];
  for (const entity_type of ENTITY_TYPE_ORDER) {
    for (const row of data[entity_type]) {
      flat.push({ entity_type, row });
    }
  }
  return flat;
}


function _isHotkeyOpen(event: KeyboardEvent): boolean {
  if (event.key !== "k" && event.key !== "K") return false;
  return event.metaKey || event.ctrlKey;
}


export function GlobalSearchModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [rawInput, setRawInput] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  // ── Open / close ─────────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (_isHotkeyOpen(event)) {
        event.preventDefault();
        setIsOpen(true);
        return;
      }
      if (event.key === "Escape" && isOpen) {
        event.preventDefault();
        setIsOpen(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  // Reset state every time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setRawInput("");
      setDebounced("");
      setActiveIndex(0);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // ── Debounce the input ───────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handle = setTimeout(() => setDebounced(rawInput), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [rawInput, isOpen]);

  const { data, isFetching } = useGlobalSearch(debounced);

  const flat = useMemo(() => _flatten(data), [data]);

  // Reset active index when the result set changes shape.
  useEffect(() => {
    setActiveIndex(0);
  }, [flat.length]);

  // ── Selection handling ───────────────────────────────────────────
  const handleSelect = useCallback(
    (entry: FlatRow) => {
      const recent: RecentSearchEntry = {
        entity_type: entry.entity_type,
        id: entry.row.id,
        label: entry.row.label,
        sublabel: entry.row.sublabel,
        route: entry.row.route,
      };
      addRecentSearch(recent);
      setIsOpen(false);
      navigate(entry.row.route);
    },
    [navigate],
  );

  // ── Keyboard navigation inside the input ────────────────────────
  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (flat.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const active = flat[activeIndex];
      if (active) handleSelect(active);
    }
  }

  if (!isOpen) return null;

  const recents = loadRecentSearches();
  const trimmed = debounced.trim();
  const queryActive = trimmed.length >= 2;
  const showRecents = !queryActive && recents.length > 0;
  const showEmpty = queryActive && !isFetching && flat.length === 0;

  return (
    <div
      data-testid="global-search-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Search Sales CRM"
      className={[
        "fixed inset-0 z-50 flex items-start justify-center",
        "bg-black/40 px-4 pt-[12vh]",
      ].join(" ")}
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsOpen(false);
      }}
    >
      <div
        className={[
          "w-full max-w-xl overflow-hidden rounded-lg",
          "border border-border-subtle bg-elevated shadow-xl",
        ].join(" ")}
      >
        <div className="border-b border-border-subtle p-3">
          <input
            ref={inputRef}
            data-testid="global-search-input"
            type="text"
            placeholder="Search prospects, opportunities, contacts, carriers…"
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            onKeyDown={onInputKeyDown}
            className={[
              "w-full bg-transparent text-sm text-text-primary",
              "placeholder:text-text-tertiary focus:outline-none",
            ].join(" ")}
          />
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {showRecents && (
            <RecentSearchesList
              entries={recents}
              onSelect={(entry) =>
                handleSelect({
                  entity_type: entry.entity_type,
                  row: {
                    id: entry.id,
                    label: entry.label,
                    sublabel: entry.sublabel,
                    route: entry.route,
                  },
                })
              }
            />
          )}

          {queryActive && (
            <ResultsList
              data={data}
              flat={flat}
              activeIndex={activeIndex}
              onSelect={handleSelect}
              onHover={setActiveIndex}
            />
          )}

          {showEmpty && (
            <p
              data-testid="global-search-empty"
              className="p-6 text-center text-xs text-text-secondary"
            >
              No matches for "{trimmed}".
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function RecentSearchesList({
  entries, onSelect,
}: {
  entries: RecentSearchEntry[];
  onSelect: (entry: RecentSearchEntry) => void;
}) {
  return (
    <div data-testid="global-search-recent">
      <div
        className={[
          "border-b border-border-subtle bg-recessed px-3 py-1.5",
          "text-[10px] font-medium uppercase tracking-wide",
          "text-text-tertiary",
        ].join(" ")}
      >
        Recent
      </div>
      <ul role="listbox">
        {entries.map((e) => (
          <li
            key={`${e.entity_type}-${e.id}`}
            role="option"
            aria-selected={false}
            tabIndex={0}
            onClick={() => onSelect(e)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") onSelect(e);
            }}
            className={[
              "cursor-pointer px-3 py-2 text-sm",
              "hover:bg-recessed",
            ].join(" ")}
          >
            <div className="text-text-primary">{e.label}</div>
            {e.sublabel && (
              <div className="text-xs text-text-tertiary">{e.sublabel}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}


function ResultsList({
  data, flat, activeIndex, onSelect, onHover,
}: {
  data: ReturnType<typeof useGlobalSearch>["data"];
  flat: FlatRow[];
  activeIndex: number;
  onSelect: (entry: FlatRow) => void;
  onHover: (index: number) => void;
}) {
  if (!data) return null;
  return (
    <div role="listbox">
      {ENTITY_TYPE_ORDER.map((entity_type) => {
        const rows = data[entity_type];
        if (rows.length === 0) return null;
        return (
          <div
            key={entity_type}
            data-testid={`global-search-group-${entity_type}`}
          >
            <div
              className={[
                "sticky top-0 z-10 border-b border-border-subtle",
                "bg-recessed px-3 py-1.5",
                "text-[10px] font-medium uppercase tracking-wide",
                "text-text-tertiary",
              ].join(" ")}
            >
              {ENTITY_TYPE_LABEL[entity_type]}
            </div>
            <ul>
              {rows.map((row) => {
                const idx = flat.findIndex(
                  (f) => f.entity_type === entity_type && f.row.id === row.id,
                );
                const active = idx === activeIndex;
                return (
                  <li
                    key={`${entity_type}-${row.id}`}
                    role="option"
                    data-testid={`global-search-row-${entity_type}-${row.id}`}
                    aria-selected={active}
                    onMouseEnter={() => onHover(idx)}
                    onClick={() => onSelect({ entity_type, row })}
                    className={[
                      "cursor-pointer px-3 py-2 text-sm",
                      active ? "bg-recessed" : "",
                    ].join(" ")}
                  >
                    <div className="text-text-primary">{row.label}</div>
                    {row.sublabel && (
                      <div className="text-xs text-text-tertiary">
                        {row.sublabel}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
